// crmClaims — the CRM's per-user claim vocabulary and the ONE question every
// gate asks: "may the current operator do X?"
//
// Spec: CLM-07b (`Global-Common-Backend-Packages/packages/identity-sdk/docs/CLM-crm-claims.md`).
//
// ── WHERE THE IDS COME FROM ────────────────────────────────────────────────
// The catalogue is owned by `@rheora/identity-sdk` (`src/claims/catalogue.ts`,
// CLM-01), which every backend guard evaluates. The CRM cannot import a backend
// package, so the list below is a mirror, and `__fixtures__/crm-claims.fixture.json`
// is a verbatim copy of the SDK's generated fixture. `__tests__/crmClaims.test.ts`
// fails if this file and that fixture disagree in any id, module, label,
// sensitivity, preset or module label. Changing a claim: change the SDK, copy its
// fixture here, then make this file match.
//
// ── HOW A GATE DECIDES ─────────────────────────────────────────────────────
// Three principals, in order:
//   1. `platform:super-admin` (raw scope) — every claim, exactly as the backend's
//      `evaluateClaim` allows it. `super-admin-readonly`, `support-agent` and
//      `country:*` do NOT bypass.
//   2. A token with `crm_perms_v` — its `crm_perms` and nothing else.
//      `@fluxora/auth` has already dropped claims whose version is bound to
//      another tenant, so `claims: []` with a version is a real empty set, never
//      "fall back to roles". (An empty claim set omits `crm_perms` entirely but
//      keeps `crm_perms_v` — measured on the real realm, 2026-11-03.)
//   3. A token WITHOUT `crm_perms_v` (issued before claims, or the dev dummy
//      provider) — the legacy role fallback below. It exists only for the
//      rollout window; once every user is backfilled no live token takes it.
//
// No claim string belongs in JSX: pages import the typed ids from here.

import { useContext } from 'react';

import { AuthContext, type AuthUser } from '@fluxora/auth';

// --- Catalogue (mirror of @rheora/identity-sdk/claims) -----------------------

export const CRM_CLAIM_MODULES = [
  'accounts',
  'prospects',
  'quotes',
  'billing',
  'payments',
  'pricing',
  'metering',
  'marketops',
  'reports',
  'data',
  'users',
  'settings',
  'brokers',
  'ai',
] as const;
export type CrmClaimModule = (typeof CRM_CLAIM_MODULES)[number];

/**
 * English module names, mirrored from the SDK fixture for the parity test.
 * Screens render module names through `t()`; this map is the contract value,
 * not display copy.
 */
export const CRM_CLAIM_MODULE_LABELS: Readonly<Record<CrmClaimModule, string>> = {
  accounts: 'Accounts',
  prospects: 'Prospects',
  quotes: 'Contracts and Signups',
  billing: 'Billing',
  payments: 'Payments',
  pricing: 'Pricing',
  metering: 'Metering',
  marketops: 'Market Operations',
  reports: 'Reports',
  data: 'Data export',
  users: 'Users',
  settings: 'Settings',
  brokers: 'Brokers',
  ai: 'AI assistant',
};

export interface CrmClaimDef {
  readonly id: string;
  readonly module: CrmClaimModule;
  /** English contract label (SDK parity). */
  readonly label: string;
  /**
   * What the claim lets the holder do, in plain language, for the admin who
   * assigns it. Catalogue DATA from the SDK, shown as-is (not a `t()` key).
   */
  readonly description: string;
  /** Moves money, changes a contract, exposes PII or sends data out. */
  readonly sensitive: boolean;
}

const c = <const Id extends string>(
  id: Id,
  module: CrmClaimModule,
  label: string,
  description: string,
  sensitive = false,
): CrmClaimDef & { readonly id: Id } => ({ id, module, label, description, sensitive });

export const CRM_CLAIMS = [
  c(
    'accounts.records.view',
    'accounts',
    'View accounts',
    'See customer accounts: the account list, account details, the 360 view, notes, documents, service points and enrollment status.',
  ),
  c(
    'accounts.records.manage',
    'accounts',
    'Create and edit accounts, notes, documents',
    'Create and edit customer accounts and signups: add notes and documents, update contact and signup details, record a credit decision and accept a signup.',
  ),
  c(
    'accounts.lifecycle.manage',
    'accounts',
    'Suspend, reactivate, close accounts',
    "Suspend, reactivate, close or activate a customer's account. Changes whether the customer is served and billed.",
    true,
  ),
  c(
    'accounts.portal-users.manage',
    'accounts',
    'Manage customer portal users',
    "Manage who can sign in to a customer's online portal: invite or revoke users, edit them, choose the primary user and unlock locked sign-ins.",
  ),
  c(
    'accounts.pii.reveal',
    'accounts',
    'See unmasked customer contact details',
    "See a customer's unmasked personal details: their email address, the identity given at signup and their signature. Exposes personal data.",
    true,
  ),
  c(
    'accounts.bank.reveal',
    'accounts',
    'Capture and view bank details',
    "Capture a customer's bank details, decrypt and view full account numbers, and mark bank details invalid. Exposes financial data that could be used to take money.",
    true,
  ),
  c(
    'accounts.compliance.manage',
    'accounts',
    'KYC, credit decisions, data requests',
    "Run credit checks, review and confirm identity (KYC) documents, and act on customers' data requests: access, correction, erasure, portability and restriction. Erasure permanently removes customer data.",
    true,
  ),
  c(
    'prospects.records.view',
    'prospects',
    'View leads, enquiries, switch holds',
    'See leads and unfinished signups with their timelines and stats, contact enquiries and switch-hold cases on the Prospects screens.',
  ),
  c(
    'prospects.records.manage',
    'prospects',
    'Send resume links, reply to enquiries',
    'Send a prospect a link to finish their signup, and reply to contact enquiries. Both send a message to the customer.',
  ),
  c(
    'quotes.records.view',
    'quotes',
    'View contracts and signups',
    'See contracts and signups: quotes and their history and documents, renewals, approvals, bulk quote jobs and the ESI ID lookup.',
  ),
  c(
    'quotes.records.manage',
    'quotes',
    'Create and edit signups, bulk jobs',
    'Create and edit signups: start quotes, choose a plan, change the broker or start date, link an account, and run bulk quote jobs.',
  ),
  c(
    'quotes.offers.send',
    'quotes',
    'Send and revoke offers',
    'Send an offer to a customer, and revoke an offer already sent.',
  ),
  c(
    'quotes.contracts.approve',
    'quotes',
    'Approve, accept, submit for signature, renewals',
    'Approve and accept contracts, submit them for signature, change their status, and accept or decline renewals. Commits the business to supplying the customer at the agreed price.',
    true,
  ),
  c(
    'quotes.contracts.rescind',
    'quotes',
    'Rescind or cancel contracts',
    "Rescind or cancel a customer's contract. Ends the customer's agreement.",
    true,
  ),
  c(
    'quotes.settings.manage',
    'quotes',
    'Quote expiry policy',
    'View and change how long quotes stay valid before they expire.',
  ),
  c(
    'billing.bills.view',
    'billing',
    'View bills, cycles, PDFs',
    'See bills and their PDFs, credit notes, bill cycles, billing exceptions, the billing dashboard and trends, billing calendars, scheduler runs and billing settings.',
  ),
  c(
    'billing.bills.generate',
    'billing',
    'Preview and generate bills, manage cycles',
    'Preview and generate bills, and create, edit, delete, check and run bill cycles. Sets the amounts customers are charged.',
    true,
  ),
  c(
    'billing.bills.send',
    'billing',
    'Send bills and credit notes',
    'Send bills and credit notes to customers, or mark a bill as sent.',
  ),
  c(
    'billing.bills.adjust',
    'billing',
    'Cancel, credit, reverse bills',
    'Cancel bills, issue credit notes, and reverse a bill together with its credit note. Changes what customers owe.',
    true,
  ),
  c(
    'billing.exceptions.manage',
    'billing',
    'Dismiss and reopen exceptions',
    'Dismiss billing exceptions, one at a time or in batches, and reopen dismissed ones.',
  ),
  c(
    'billing.settings.manage',
    'billing',
    'Billing settings, scheduler, calendars',
    "Change billing settings and billing calendars, including each account's calendar, and start or cancel billing scheduler runs. Affects when and how every customer is billed.",
    true,
  ),
  c(
    'payments.records.view',
    'payments',
    'View payments, worklist, plans',
    'See payments, the payments worklist, ACH debits, refunds, bank mandates, AutoPay, payment methods, payment plans and levelized billing.',
  ),
  c(
    'payments.collect.manage',
    'payments',
    'Take payments, ACH actions, fees',
    'Record a payment, mark an ACH debit as returned or present it again, and charge a late-payment fee.',
  ),
  c(
    'payments.refunds.issue',
    'payments',
    'Issue refunds and void payments',
    "Issue refunds to a customer's payment method and void payments. Moves money out of the business.",
    true,
  ),
  c(
    'payments.plans.manage',
    'payments',
    'AutoPay, methods, payment and levelized plans',
    'Set up and change how customers pay: AutoPay, saved payment methods, bank mandates, deferred payment plans and levelized billing.',
  ),
  c(
    'pricing.catalog.view',
    'pricing',
    'View pricing',
    'See the Pricing screens: tariffs and their previews, plans, rate cards, the forward curve, pricing configuration, charges, caps, margins, tax and levy rates, and wholesale price history.',
  ),
  c(
    'pricing.catalog.manage',
    'pricing',
    'Edit tariffs, rate cards, pricing config',
    'Create, edit and delete draft tariffs, plans and pricing configuration: network charges, regulatory caps, operating costs, margin rules, tax and levy rates. Also runs price data imports and the broker commission report.',
  ),
  c(
    'pricing.tariffs.publish',
    'pricing',
    'Publish, approve, reject tariffs',
    "Publish, approve or reject tariffs and change a plan's status. Puts prices in front of customers.",
    true,
  ),
  c(
    'metering.meters.view',
    'metering',
    'View meters, reads, usage',
    'See meters with their events, reads, interval data, usage and validation results on the Metering screens.',
  ),
  c(
    'metering.meters.manage',
    'metering',
    'Meter lifecycle, ingestion, jobs, TDSP mappings',
    "Add meters, change a meter's lifecycle state, generate reads and interval data, run ingestion, aggregation and scheduled jobs, and see TDSP mappings.",
  ),
  c(
    'marketops.console.view',
    'marketops',
    'View market console and enrollment issues',
    'See the Market Operations console: enrollment status and issues, service orders, disputes, exceptions, the market inbox and the webhook delivery log.',
  ),
  c(
    'marketops.messages.send',
    'marketops',
    'Send market responses',
    "Answer market requests on the market's behalf: accept or reject them, send meter reads and emit usage. Changes what the system records as the market's reply.",
    true,
  ),
  c(
    'marketops.enrollments.manage',
    'marketops',
    'Cancel, maintain, POLR, WTM',
    "Enrol, cancel and maintain service with the market: move-outs, service orders, new-build service points, rescheduling, POLR mass transitions and weather moratoriums. Can start or stop a customer's supply.",
    true,
  ),
  c(
    'marketops.disputes.manage',
    'marketops',
    'Disputes, exceptions, invoice rejects',
    'Raise and update disputes, acknowledge and resolve market exceptions, reject market invoices and record remittances.',
  ),
  c(
    'marketops.admin.manage',
    'marketops',
    'Webhook endpoints, outbox retry, GDPR',
    'Manage webhook endpoints and retry failed deliveries, change market simulator settings and auto-responses, and run privacy and compliance actions. Affects every system that receives market events.',
    true,
  ),
  c(
    'reports.all.view',
    'reports',
    'View reports',
    'See reports: AR aging, bills, payments, usage variance, quote performance, renewal risk and dashboard funnels, plus market invoices and wholesale data.',
  ),
  c(
    'data.export.run',
    'data',
    'Download workbooks and CSVs',
    'Download account exports and bulk quote results as workbooks or CSV files. Takes customer data out of the CRM.',
    true,
  ),
  c(
    'users.records.view',
    'users',
    'View CRM users',
    "See the list of CRM users and each user's claims.",
  ),
  c(
    'users.records.manage',
    'users',
    'Invite, suspend, remove CRM users',
    'Invite CRM users, resend invitations, edit or suspend users, and remove them. Controls who can sign in to the CRM.',
    true,
  ),
  c(
    'users.claims.manage',
    'users',
    'Assign claims to users',
    'Grant and remove claims for other CRM users, but only claims you hold yourself. Controls what everyone else can see and do.',
    true,
  ),
  c(
    'users.audit.view',
    'users',
    'View the audit log',
    'See the audit log of who changed what in the CRM.',
  ),
  c(
    'settings.templates.manage',
    'settings',
    'Document and email templates',
    'View, edit, preview, check and activate document and email templates. Changes what every customer receives.',
  ),
  c('brokers.records.view', 'brokers', 'View brokers', 'See the list of brokers.'),
  c(
    'brokers.records.manage',
    'brokers',
    'Create and edit brokers and agents',
    'Create and edit brokers and their agents.',
  ),
  c(
    'ai.assistant.use',
    'ai',
    'Use the AI assistant',
    'Show the AI assistant in the CRM. This only shows or hides it; it does not limit what data the assistant can read.',
  ),
] as const;

/** A CRM claim id, e.g. `'billing.bills.generate'`. */
export type CrmClaim = (typeof CRM_CLAIMS)[number]['id'];

export const CRM_CLAIM_IDS: readonly CrmClaim[] = CRM_CLAIMS.map((d) => d.id);

const ID_SET: ReadonlySet<string> = new Set(CRM_CLAIM_IDS);

export function isCrmClaim(value: unknown): value is CrmClaim {
  return typeof value === 'string' && ID_SET.has(value);
}

/** Catalogue entry for an id. Every `CrmClaim` has one, so this never misses. */
export function claimDef(id: CrmClaim): CrmClaimDef {
  const def = CRM_CLAIMS.find((d) => d.id === id);
  if (def === undefined) throw new Error(`unknown CRM claim ${id}`);
  return def;
}

export const CRM_PRESETS = ['admin', 'agent', 'viewer'] as const;
export type CrmPreset = (typeof CRM_PRESETS)[number];

const VIEWER: readonly CrmClaim[] = [
  'accounts.records.view',
  'prospects.records.view',
  'quotes.records.view',
  'billing.bills.view',
  'payments.records.view',
  'metering.meters.view',
  'brokers.records.view',
];

const AGENT: readonly CrmClaim[] = [
  ...VIEWER,
  'accounts.records.manage',
  'accounts.pii.reveal',
  'prospects.records.manage',
  'quotes.records.manage',
  'quotes.offers.send',
  'billing.bills.send',
  'billing.exceptions.manage',
  'payments.collect.manage',
  'payments.plans.manage',
  'pricing.catalog.view',
];

/** Default claim set per preset. A user's real set may be customised. */
export const CRM_PRESET_CLAIMS: Readonly<Record<CrmPreset, readonly CrmClaim[]>> = {
  admin: CRM_CLAIM_IDS,
  agent: AGENT,
  viewer: VIEWER,
};

// --- Principal → effective claim set (pure) ---------------------------------

/** The raw scope that bypasses every claim, mirroring the backend guard. */
export const SUPER_ADMIN_SCOPE = 'platform:super-admin';

/** The slice of `AuthUser` a claim decision reads. */
export type ClaimPrincipal = Pick<AuthUser, 'claims' | 'claimsVersion' | 'roles' | 'scopes'>;

/** How a principal's claims were decided — for diagnostics and tests. */
export type ClaimSource = 'none' | 'super-admin' | 'token' | 'legacy-roles';

/**
 * LEGACY ROLE FALLBACK — a token without `crm_perms_v` only.
 *
 * The coarse `UserRole`s `@fluxora/auth` projects (plus the domain roles the old
 * route matrix named) map onto the presets, so a pre-claims token sees roughly
 * what its role saw before. `user` maps to nothing: the token mapper turns any
 * unrecognised scope into `user`, so it says nothing about the operator. The
 * ROUTE gate does not use this table — while a token is legacy it keeps the old
 * per-prefix role list verbatim (see `crmRouteAccess.ts`).
 */
export const LEGACY_ROLE_CLAIMS: Readonly<Record<string, readonly CrmClaim[]>> = {
  admin: CRM_CLAIM_IDS,
  manager: AGENT,
  viewer: VIEWER,
  pricing: ['pricing.catalog.view', 'pricing.catalog.manage'],
  billing: [
    'billing.bills.view',
    'billing.bills.generate',
    'billing.bills.send',
    'billing.bills.adjust',
    'billing.exceptions.manage',
    'billing.settings.manage',
    'payments.records.view',
    'payments.collect.manage',
    'payments.plans.manage',
    'reports.all.view',
  ],
  ops: [
    'metering.meters.view',
    'payments.records.view',
    'payments.collect.manage',
    'marketops.console.view',
    'marketops.messages.send',
  ],
  metering: ['metering.meters.view', 'metering.meters.manage'],
  accounts: [
    'accounts.records.view',
    'accounts.records.manage',
    'prospects.records.view',
    'prospects.records.manage',
    'brokers.records.view',
  ],
  quotes: [
    'quotes.records.view',
    'quotes.records.manage',
    'quotes.offers.send',
    'accounts.records.view',
    'prospects.records.view',
    'brokers.records.view',
  ],
};

/** True when the token predates claims and gates must use the role fallback. */
export function isLegacyPrincipal(p: ClaimPrincipal): boolean {
  return p.claimsVersion === null && !p.scopes.includes(SUPER_ADMIN_SCOPE);
}

/** Where a principal's claims come from. `null` (signed out) is `'none'`. */
export function claimSourceOf(p: ClaimPrincipal | null): ClaimSource {
  if (p === null) return 'none';
  if (p.scopes.includes(SUPER_ADMIN_SCOPE)) return 'super-admin';
  if (p.claimsVersion !== null) return 'token';
  return 'legacy-roles';
}

/**
 * The claims the principal holds. Unknown strings in the token are dropped (a
 * claim the CRM does not know cannot unlock anything in it).
 */
export function effectiveClaims(p: ClaimPrincipal | null): ReadonlySet<CrmClaim> {
  switch (claimSourceOf(p)) {
    case 'none':
      return new Set();
    case 'super-admin':
      return new Set(CRM_CLAIM_IDS);
    case 'token':
      return new Set((p?.claims ?? []).filter(isCrmClaim));
    case 'legacy-roles': {
      const out = new Set<CrmClaim>();
      for (const role of p?.roles ?? []) {
        for (const claim of LEGACY_ROLE_CLAIMS[role] ?? []) out.add(claim);
      }
      return out;
    }
  }
}

/**
 * Does the principal hold the claim — or, for an array, ANY of them (the
 * backend rule shape is `anyOf`)? An empty array is `false`: a gate that names
 * no claim is a bug, and it fails closed.
 */
export function hasClaim(
  p: ClaimPrincipal | null,
  required: CrmClaim | readonly CrmClaim[],
): boolean {
  const list: readonly CrmClaim[] = typeof required === 'string' ? [required] : required;
  if (list.length === 0) return false;
  const held = effectiveClaims(p);
  return list.some((claim) => held.has(claim));
}

// --- React hooks --------------------------------------------------------------

/**
 * The signed-in principal, or null. Reads the context directly rather than via
 * `useAuth()` so a component rendered outside an auth provider FAILS CLOSED
 * (every gate false) instead of throwing. In the app every claim-gated control
 * sits under `RequireAuth`, so this only matters for isolated renders.
 */
export function useClaimPrincipal(): ClaimPrincipal | null {
  return useContext(AuthContext)?.user ?? null;
}

/**
 * Does the signed-in operator hold `required` (or any of an array)?
 *
 * Hide what this returns `false` for — never render a control the backend
 * would refuse with 403 `CLAIM_REQUIRED`.
 */
export function useHasClaim(required: CrmClaim | readonly CrmClaim[]): boolean {
  return hasClaim(useClaimPrincipal(), required);
}

/** The operator's whole effective claim set (e.g. to grey what an admin lacks). */
export function useEffectiveClaims(): ReadonlySet<CrmClaim> {
  return effectiveClaims(useClaimPrincipal());
}

/** Where the operator's claims come from. */
export function useClaimSource(): ClaimSource {
  return claimSourceOf(useClaimPrincipal());
}
